import { toLabelValue } from '@/common/convertToDropdownOptions';
import { Form, useForm } from '@/components/forms';
import Input from '@/components/forms/Input';
import Select from '@/components/forms/Select';
import { useDropdown } from '@/hooks/useDropdown';
import { useDynamicLocale } from '@/hoslog-intl';
import { AdminStaff } from '@/services/adminStaff/adminStaff.types';
import { useEffect } from 'react';
import { z } from 'zod';
import useAddAdminStaff from './hooks/useAddAdminStaff';
import useEditAdminStaff from './hooks/useEditAdminStaff';

const addStaffModalSchema = z.object({
  role: z.string().trim().min(1, { message: 'Required' }),
  userName: z.string().trim().min(1, { message: 'Required' }),
  email: z.string().trim().min(1, { message: 'Required' }).email(),
  contactNumber: z
    .string()
    .trim()
    .min(1, { message: 'Required' })
    .regex(/^\d{10,11}$/, { message: 'Please enter a valid phone number' }),
  status: z.string().trim().min(1, { message: 'Required' }),
});

interface AdminStaffFormProps {
  data?: AdminStaff;
}

export const AddStaffForm = ({ data }: AdminStaffFormProps) => {
  const form = useForm({ schema: addStaffModalSchema, mode: 'onBlur', reValidateMode: 'onBlur' });
  const isEdit = data ? true : false;
  const { t } = useDynamicLocale();
  const { data: roleType } = useDropdown('role');
  const { data: availableRoles } = useDropdown('roleType', roleType?.[0]?.value ?? '');
  const { mutateAsync } = useAddAdminStaff();
  const { mutateAsync: editAdminStaff } = useEditAdminStaff();

  const roleOptions = toLabelValue(availableRoles ?? [], {
    labelKey: 'label',
    valueKey: 'value',
  });

  const statusOptions = [
    { label: 'Active', value: 'A' },
    { label: 'Blocked', value: 'B' },
  ];

  useEffect(() => {
    if (data) {
      form.reset({
        userName: data?.username,
        email: data?.emailAddress,
        contactNumber: data?.phoneNumber ? String(data?.phoneNumber) : '',
        role: data?.roleId,
        status: data?.status,
      });
    }
  }, [data]);

  return (
    <Form
      form={form}
      onSubmit={async (formData) => {
        const payload = {
          userName: formData.userName.trim(),
          emailAddress: formData.email.trim(),
          phoneNumber: formData.contactNumber.trim(),
          role: formData.role.trim(),
          status: formData.status.trim(),
        };
        if (!isEdit) {
          await mutateAsync(payload);
        } else {
          await editAdminStaff({ ...payload, staffId: data?.staffId ?? '' });
        }
      }}
    >
      <div className="flex flex-col gap-4.5">
        <Input
          label="common.page.modal.username"
          {...form.register('userName')}
          placeholder={t('common.page.modal.usernamePlaceholder')}
          disabled={!!data?.username}
        />
        <Input
          label="common.page.modal.emailAddress"
          {...form.register('email')}
          placeholder={t('common.page.modal.emailAddressPlaceholder')}
        />
        <Input
          label="common.page.modal.phoneNumber"
          {...form.register('contactNumber')}
          placeholder={t('common.page.modal.phoneNumberPlaceholder')}
          type="text"
          inputMode="numeric"
          onlyNumbers
          maxLength={11}
        />
        <div>
          <Select
            label="common.page.modal.role"
            {...form.register('role')}
            placeholder={t('common.page.modal.rolePlaceholder')}
            options={roleOptions}
            selected={data?.roleId}
          />
        </div>
        <div>
          <Select
            label="common.page.modal.status"
            {...form.register('status')}
            placeholder={t('common.page.modal.statusPlaceholder')}
            options={statusOptions}
            selected={data?.status}
          />
        </div>
      </div>
    </Form>
  );
};
